import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Textarea,
  useToast,
  SimpleGrid,
  Divider,
} from '@chakra-ui/react';
import { getAuth, User } from 'firebase/auth';
import { app } from '../../configs/firebaseApp';
import { useCreateCustomerMutation } from '../../redux/payments/payments';

export const CreateCustomerScreen: React.FC = () => {
  const toast = useToast();
  const auth = getAuth(app);

  const firebaseUser = auth.currentUser;

  const [user, setUser] = useState<User | null>(firebaseUser);
  // Remove console.log for production
  // console.log(user);

  interface CustomerInfo {
    id: string;
    uid: string;
    createdAt: Date;
    updatedAt: Date;
    isPaidCustomer: boolean;
    payments: any[]; // Replace 'any' with a more specific type if available
    customerId: string;
    email: string;
    name: string;
    phone: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
    billingEmail: string;
    metadata: string;
    type: string;
    photoUrl: string;
  }

  const [formData, setFormData] = useState<
    Omit<
      CustomerInfo,
      | 'id'
      | 'uid'
      | 'createdAt'
      | 'updatedAt'
      | 'isPaidCustomer'
      | 'payments'
      | 'customerId'
    >
  >({
    email: '',
    name: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
    },
    billingEmail: '',
    metadata: '',
    type: 'individual',
    photoUrl: '',
  });

  const [createCustomer, { isLoading: isCreatingCustomer }] = useCreateCustomerMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [addressField]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // @ts-ignore
      const result = await createCustomer(formData).unwrap();
      console.log(result);
      toast({
        title: 'Customer created.',
        description: "We've created your customer for you.",
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error creating customer:', error);
      toast({
        title: 'Error',
        description: 'There was an error creating the customer. Please try again.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <Box maxWidth="900px" margin="auto" mt={8} px={4}>
      <Heading mb={6}>Create Customer</Heading>
      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isDisabled
              />
            </FormControl>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Billing Email</FormLabel>
              <Input
                type="email"
                name="billingEmail"
                value={formData.billingEmail}
                onChange={handleChange}
              />
            </FormControl>
          </SimpleGrid>

          <Divider my={4} />

          <Heading size="md" mb={2}>
            Address
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl>
              <FormLabel>Line 1</FormLabel>
              <Input
                type="text"
                name="address.line1"
                value={formData.address.line1}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Line 2</FormLabel>
              <Input
                type="text"
                name="address.line2"
                value={formData.address.line2}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>City</FormLabel>
              <Input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>State</FormLabel>
              <Input
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Postal Code</FormLabel>
              <Input
                type="text"
                name="address.postal_code"
                value={formData.address.postal_code}
                onChange={handleChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Country</FormLabel>
              <Input
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
              />
            </FormControl>
          </SimpleGrid>

          <FormControl>
            <FormLabel>Metadata</FormLabel>
            <Textarea
              name="metadata"
              value={formData.metadata}
              onChange={handleChange}
              rows={4}
            />
          </FormControl>
          <Button type="submit" colorScheme="blue" size="lg">
            Create Customer
          </Button>
        </VStack>
      </form>
    </Box>
  );
};
